/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Featured Sectors logic module.
 * Handles fetching featured sectors data for the dashboard display.
 * @module logic/featuredSectors
 */

import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { refreshToken, signOut } from "../../utils/common.js";

/**
 * Fetches featured sectors for dashboard display.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of featured sector objects
 */
export const getFeaturedSectors = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}dashboard/featuredSectors`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getFeaturedSectors(navigate);
    }

    return data;
  } catch (error) {
    logger.error("Error fetching featured sectors:", {
      err: sanitizeError(error),
    });
    return [];
  }
};
